import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import Seo from '@/components/Seo';
import { useI18n } from '@/i18n/LanguageContext';
import { invokeEdge } from '@/lib/edgeInvoke';
import { zipShapeCheck, zipErrorKey, ZIP_COPY } from '@/lib/geoAlerts';
import { zipStateMismatch, statesForZip } from '@/data/zipPrefixStates';

/**
 * ONE-TIME ZIP CONFIRMATION (2026-10-09) — linked from the geo-zip-confirm email.
 * No login. The token is a single-use capability; the server (geo-zip-confirm)
 * re-validates every ZIP with mkt_listing_zip_problem() and the listing trigger,
 * so the checks below are only for instant feedback. noindex.
 */
interface Item { id: string; title: string; city: string | null; state: string | null }

const COPY = {
  title: { en: 'Confirm your project ZIP code', es: 'Confirme el código postal de su proyecto' },
  intro: {
    en: 'Enter the real 5-digit ZIP code where the work will be done, so nearby contractors can find your project. Nothing else about your project changes.',
    es: 'Ingrese el código postal real de 5 dígitos donde se hará el trabajo, para que los contratistas cercanos encuentren su proyecto. Nada más de su proyecto cambia.',
  },
  save: { en: 'Save ZIP code', es: 'Guardar código postal' },
  saving: { en: 'Saving…', es: 'Guardando…' },
  done: { en: 'Thank you — your ZIP code is saved. Nearby contractors can now find your project.', es: 'Gracias, su código postal quedó guardado. Los contratistas cercanos ya pueden encontrar su proyecto.' },
  invalid: { en: 'This link is not valid. Please use the button in the email we sent you.', es: 'Este enlace no es válido. Use el botón del correo que le enviamos.' },
  used: { en: 'This link was already used, so your ZIP code is already saved. Thank you!', es: 'Este enlace ya se usó, así que su código postal ya está guardado. ¡Gracias!' },
  expired: { en: 'This link has expired. Please contact support@giggone.com and we will help you update the ZIP.', es: 'Este enlace venció. Escriba a support@giggone.com y le ayudaremos a actualizar el código postal.' },
  dashboard: { en: 'Go to my dashboard', es: 'Ir a mi panel' },
  zip: { en: 'ZIP code', es: 'Código postal' },
} as const;

const ConfirmZip: React.FC = () => {
  const { lang, path } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const L = (k: keyof typeof COPY) => COPY[k][lang === 'es' ? 'es' : 'en'];
  const zipMsg = (k: keyof typeof ZIP_COPY) => ZIP_COPY[k][lang === 'es' ? 'es' : 'en'];

  const [state, setState] = useState<'loading' | 'ready' | 'saving' | 'done' | 'invalid' | 'used' | 'expired'>('loading');
  const [items, setItems] = useState<Item[]>([]);
  const [zips, setZips] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!/^[0-9a-f]{64}$/.test(token)) { setState('invalid'); return; }
    let alive = true;
    void (async () => {
      const res = await invokeEdge('geo-zip-confirm', { action: 'lookup', token }, { requireAuth: false });
      if (!alive) return;
      if (res.ok && (res.data as any)?.listings) {
        setItems((res.data as any).listings);
        setState('ready');
      } else if (res.error === 'already_used') setState('used');
      else if (res.error === 'expired') setState('expired');
      else setState('invalid');
    })();
    return () => { alive = false; };
  }, [token]);

  const localProblem = (it: Item, raw: string): string | null => {
    const shape = zipShapeCheck(raw);
    if (shape === 'empty') return zipMsg('required');
    if (shape === 'format') return zipMsg('format');
    if (shape === 'placeholder') return zipMsg('placeholder');
    if (zipStateMismatch(raw, it.state)) return `${zipMsg('stateMismatch')} (${raw.slice(0, 5)} → ${statesForZip(raw).join('/')})`;
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const errs: Record<string, string> = {};
    for (const it of items) {
      const p = localProblem(it, (zips[it.id] || '').trim());
      if (p) errs[it.id] = p;
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState('saving');
    const payload: Record<string, string> = {};
    for (const it of items) payload[it.id] = (zips[it.id] || '').trim();
    const res = await invokeEdge('geo-zip-confirm', { action: 'submit', token, zips: payload }, { requireAuth: false });
    if (res.ok && (res.data as any)?.success) {
      const serverErrs = ((res.data as any)?.errors || {}) as Record<string, string>;
      if (Object.keys(serverErrs).length) {
        const mapped: Record<string, string> = {};
        for (const [id, code] of Object.entries(serverErrs)) { const k = zipErrorKey(code); mapped[id] = k ? zipMsg(k) : code; }
        setErrors(mapped);
      }
      setState('done');
      return;
    }
    if (res.error === 'already_used') return setState('used');
    if (res.error === 'expired') return setState('expired');
    const serverErrs = ((res.data as any)?.errors || {}) as Record<string, string>;
    const mapped: Record<string, string> = {};
    for (const [id, code] of Object.entries(serverErrs)) { const k = zipErrorKey(code); mapped[id] = k ? zipMsg(k) : code; }
    setErrors(mapped);
    if (!Object.keys(mapped).length) setFormError(res.message || L('invalid'));
    setState('ready');
  };

  const notice = (tone: 'ok' | 'warn', text: string) => (
    <div className={`mt-6 flex items-start gap-3 rounded-2xl border p-5 ${tone === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
      {tone === 'ok' ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />}
      <div>
        <p className="text-sm font-semibold">{text}</p>
        <Link to={path('/dashboard')} className="mt-3 inline-block text-sm font-bold underline">{L('dashboard')}</Link>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:py-16">
      <Seo title={L('title')} description={L('intro')} noindex />
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-100 text-orange-600"><MapPin className="h-5 w-5" /></span>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{L('title')}</h1>
      </div>

      {state === 'loading' && <p className="mt-8 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />…</p>}
      {state === 'invalid' && notice('warn', L('invalid'))}
      {state === 'used' && notice('ok', L('used'))}
      {state === 'expired' && notice('warn', L('expired'))}
      {state === 'done' && notice('ok', L('done'))}

      {(state === 'ready' || state === 'saving') && (
        <form onSubmit={submit} className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <p className="text-sm leading-relaxed text-slate-600">{L('intro')}</p>
          <div className="mt-5 space-y-5">
            {items.map((it) => {
              const val = zips[it.id] || '';
              const live = val && zipShapeCheck(val) === 'shape_ok' && zipStateMismatch(val, it.state);
              return (
                <div key={it.id} className="rounded-xl border border-slate-200 p-4">
                  <p className="font-bold text-slate-900">{it.title}</p>
                  <p className="text-sm text-slate-500">{[it.city, it.state].filter(Boolean).join(', ')}</p>
                  <label htmlFor={`zip-${it.id}`} className="mt-3 block text-xs font-bold uppercase tracking-wide text-slate-500">{L('zip')}</label>
                  <input
                    id={`zip-${it.id}`}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={10}
                    placeholder="12345"
                    value={val}
                    onChange={(e) => { setZips((p) => ({ ...p, [it.id]: e.target.value.replace(/[^\d-]/g, '').slice(0, 10) })); setErrors((p) => ({ ...p, [it.id]: '' })); }}
                    className={`mt-1.5 w-40 rounded-lg border px-3 py-2.5 font-mono text-base ${errors[it.id] || live ? 'border-red-400' : 'border-slate-300'}`}
                  />
                  {(errors[it.id] || live) && (
                    <p role="alert" className="mt-1.5 flex items-start gap-1.5 text-xs text-red-700">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {errors[it.id] || `${zipMsg('stateMismatch')} (${val.slice(0, 5)} → ${statesForZip(val).join('/')})`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {formError && <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          <button type="submit" disabled={state === 'saving'} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 disabled:opacity-50">
            {state === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
            {state === 'saving' ? L('saving') : L('save')}
          </button>
        </form>
      )}
    </div>
  );
};

export default ConfirmZip;
